/* ── Category Spotlight: hero slide carousel, product pager, device fitment ──
   Fitment matching delegates to the shared window.LakiFitment module (see
   assets/device-fitment-matching.js, loaded before this file) so every
   section that needs it shares one tag-matching implementation.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  const initFitment = (section) => {
    const cards = section.querySelectorAll('.csp-product[data-product-tags], .csp-product[data-product-fitment]');
    if (!cards.length) return;

    const apply = () => window.LakiFitment.applyChips(section);

    apply();
    window.addEventListener('device:selected', apply);
    window.addEventListener('device:garage-changed', apply);
  };

  const initHeroCarousel = (section) => {
    const carousel = section.querySelector('[data-csp-carousel]');
    if (!carousel) return;

    const slides = Array.from(carousel.querySelectorAll('[data-csp-slide-media]'));
    const copies = Array.from(carousel.querySelectorAll('[data-csp-slide-content]'));
    const status = carousel.querySelector('[data-csp-slide-status]');
    const prevBtn = carousel.querySelector('[data-csp-prev-slide]');
    const nextBtn = carousel.querySelector('[data-csp-next-slide]');
    if (slides.length < 2) return;

    let index = 0;

    const render = () => {
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      copies.forEach((copy, i) => copy.classList.toggle('is-active', i === index));
      if (status) status.textContent = `${index + 1} / ${slides.length}`;
    };

    prevBtn?.addEventListener('click', () => {
      index = (index - 1 + slides.length) % slides.length;
      render();
    });

    nextBtn?.addEventListener('click', () => {
      index = (index + 1) % slides.length;
      render();
    });

    render();
  };

  /* Same localStorage list main-product.liquid's "Save for later" button reads
     and writes — keeps a device/product saved here showing as saved there too. */
  const WISHLIST_KEY = 'laki:saved-products';

  const readWishlist = () => {
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (list) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (_) {
      /* no-op */
    }
  };

  const initWishlist = (section) => {
    const buttons = section.querySelectorAll('[data-wishlist-toggle]');
    if (!buttons.length) return;

    const render = (button) => {
      const saved = readWishlist().some(item=>(typeof item==='string'?item:String(item.id||''))===String(button.dataset.productId));
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    };

    buttons.forEach((button) => {
      render(button);
      button.addEventListener('click', () => {
        const productId = String(button.dataset.productId);
        const list = readWishlist();
        const index = list.findIndex(item=>(typeof item==='string'?item:String(item.id||''))===productId);
        if (index > -1) {
          list.splice(index, 1);
        } else {
          list.push({
            id: productId,
            handle: button.dataset.productHandle || '',
            title: button.dataset.productTitle || '',
            price: button.dataset.productPrice || '',
            comparePrice: button.dataset.productComparePrice || '',
            image: button.dataset.productImage || '',
            url: button.dataset.productUrl || '',
            variantId: button.dataset.productVariant || ''
          });
        }
        writeWishlist(list);
        window.dispatchEvent(new CustomEvent('laki:saved-updated'));
        render(button);
      });
    });
  };

  const initProductPager = (section) => {
    const list = section.querySelector('[data-csp-products]');
    const pager = section.querySelector('[data-csp-pager]');
    if (!list || !pager) return;

    const items = Array.from(list.querySelectorAll('[data-csp-product]'));
    const pageCount = items.reduce((max, item) => Math.max(max, Number(item.dataset.page) + 1), 1);
    const prevBtn = pager.querySelector('[data-csp-prev]');
    const nextBtn = pager.querySelector('[data-csp-next]');
    if (pageCount < 2) return;

    let page = 0;

    const render = () => {
      items.forEach((item) => {
        item.hidden = Number(item.dataset.page) !== page;
      });
      if (prevBtn) prevBtn.disabled = page === 0;
      if (nextBtn) nextBtn.disabled = page === pageCount - 1;
    };

    prevBtn?.addEventListener('click', () => {
      if (page > 0) {
        page -= 1;
        render();
      }
    });

    nextBtn?.addEventListener('click', () => {
      if (page < pageCount - 1) {
        page += 1;
        render();
      }
    });

    render();
  };

  // Prevent href="#" links (e.g. tabs with no URL set) from scrolling to top.
  const preventHashScroll = (section) => {
    section.querySelectorAll('a[href="#"]').forEach((a) => {
      a.addEventListener('click', (e) => e.preventDefault());
    });
  };

  const initSection = (section) => {
    initHeroCarousel(section);
    initProductPager(section);
    initFitment(section);
    initWishlist(section);
    preventHashScroll(section);
  };

  document.querySelectorAll('.csp-section').forEach(initSection);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.csp-section');
    if (section) initSection(section);
  });
})();
